import * as d3 from 'd3';
import uniq from 'lodash.uniq';

const parseTime = d3.timeParse("%d-%b-%Y");

const getSimulations = (asset, state) => {
  const sims = state.data.Simulations.data;

  const simsByDate = d3.nest()
    .key(d => d.Date).sortKeys(d3.ascending)
    .entries(sims.filter(d => d.Series === asset));

  const simulations = uniq(simsByDate[0].values.map(x => x.Sim));

  return simulations.map(s => ({ sim: s, values: simsByDate.map(d => ({ date: parseTime(d.key), value: d.values.find(x => x.Sim === s).Value })) }));
}

const getPercentiles = (asset, state) => {
  const perc = state.data.Percentiles.data;

  const percByDate = d3.nest()
    .key(d => d.Date).sortKeys(d3.ascending)
    .entries(perc.filter(d => d.Series === asset));

  const percentiles = uniq(percByDate[0].values.map(x => x.Percentile))
    .filter(v => [0.05, 0.25, 0.5, 0.75, 0.95].includes(v))
    .sort();

  return percentiles.map(s => ({ percentile: s, values: percByDate.map(d => ({ date: parseTime(d.key), value: d.values.find(x => x.Percentile === s).Value })) }));
}

export const getInitialDate = (state) => {
  const simulations = state.data.Simulations.data.sort((a, b) => parseTime(a.Date) - parseTime(b.Date));
  const firstDate = simulations[0].Date;

  return simulations.filter(s => s.Date !== firstDate)[0].Date;
}

export const getInitialAsset = (state) => {
  return state.data.AssetMetaData.data[0].label;
}

// TODO: Choose from available data
export const getInitialRiskStatistic = (state) => {
  return 'std';
}

// TODO: Choose from available data
export const getInitialReturnStatistic = (state) => {
  return 'mean';
}

export const getFanChartData = (asset, state) => ({
  data: {
    sims: getSimulations(asset, state),
    percentiles: getPercentiles(asset, state),
  },
  assetMetaData: getAssetMetaData(asset, state),
})

export const getRiskReturnChartData = (date, riskStatistic, returnStatistic, state) => {
  const metaData = getStatisticsMetaData(state); // Use to determine statistics ordering

  return {
    data: getStatisticsTableData(date, state).data.map(s => ({
      date: s.values[0].Date,
      asset: s.key,
      class: state.data.AssetMetaData.data.filter(d => d.label === s.key)[0].Class,
      riskStatistic: s.values[0][riskStatistic],
      returnStatistic: s.values[0][returnStatistic],
      statistics: metaData.map(s => s.statistic).map(m => ({ key: m, value: s.values[0][m] })),
    })),
    frontier: getFrontierData(date, state),
    statisticsMetaData: metaData,
  }
}

export const getDistributionChartData = (asset, date, returnStatistic, riskStatistic, state) => {
  const getRiskAndReturnStatistics = function () {
    const statisticsTableData = getStatisticsTableData(date, state).data.filter(s => s.key === asset)[0].values[0];
    return {
      returnStatistic:
      {
        key: returnStatistic,
        value: statisticsTableData[returnStatistic],
      },
      riskStatistic: {
        key: riskStatistic,
        value: riskStatistic === 'std' ? statisticsTableData['mean'] - statisticsTableData['std'] : statisticsTableData[riskStatistic],
      }
    }
  }

  return {
    data: state.data.Probabilities.data
      .filter(v => v.Date === date)
      .sort((a, b) => a.Index - b.Index)
      .map(v => ({ index: v.Index, probability: v.Probability, series: v.Series })),
    statistic: getRiskAndReturnStatistics(),
    assetMetaData: getAssetMetaData(asset, state),
    dates: getDates(asset, date, state)
  }
};

export const getStatisticsTableData = (date, state) => {
  const entries = state.data.Statistics.data.filter(x => x.Date === date);

  return {
    data: d3.nest()
      .key(x => x.Series)
      .entries(entries),
    statisticsMetaData: getStatisticsMetaData(state),
  }
}

export const getCorrelationsChartData = (date, state) => {
  return {
    data: state.data.Correlations.data.filter(x => x.Date === date)
      .sort((a, b) => a.Series2 - b.Series2),
  }
}

const getStatisticsMetaData = (state) => {
  return state.data.StatsMetaData.data
    .sort((a, b) => a.Order - b.Order)
    .map(s => ({
      statistic: s.Statistic,
      order: s.Order,
      baseline: s.Baseline,
      highGood: s.HighGood,
      isReturn: s.LevelOrRisk === 'level',
    }));
}

const getAssetMetaData = (asset, state) => {
  return state.data.AssetMetaData.data
    .sort((a, b) => a.Order - b.Order)
    .map(s => ({
      class: s.Class,
      label: s.label,
      selected: s.label === asset,
    }));
}

const getDates = (asset, date, state) => {
  // TODO: Filtering on statistic being mean is not robust so should be improved
  return state.data.Statistics.data.filter(s => s.Series === asset)
    .map(s => ({ label: s.Date, selected: s.Date === date }));
}

const getFrontierData = (date, state) => {
  return state.data.Frontiers.data.filter(f => f.Date === date)
    .sort((a, b) => a.Points - b.Points)
    .map(f => ({
      mean: f.Mean,
      std: f.Std,
    }));
}


// WEBPACK FOOTER //
// ./src/dashboards/ESG/selectors.js